import { listLpsLeads, type LpsLeadItem, type LpsLeadListQuery, type LpsLeadType } from './lps.api';

import { downloadExcel, type CsvColumn } from '@/components/list-screen/list-screen-utils';

/**
 * Which of the destination summary's two counts to export, as leads:
 *
 * - `cumulative`: handed to an agent in the window, whenever they arrived.
 * - `received`: arrived in the window, whoever holds them now.
 */
export type LpsLeadExportKind = 'cumulative' | 'received';

/** The list API's page cap. */
const PAGE_SIZE = 200;

const istDateTime = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : '';

const COLUMNS: ReadonlyArray<CsvColumn<LpsLeadItem>> = [
  { header: 'Received', value: (r) => istDateTime(r.receivedAt) },
  { header: 'Name', value: (r) => r.fullName },
  { header: 'Phone', value: (r) => r.phone },
  { header: 'Email', value: (r) => r.email },
  { header: 'Destination', value: (r) => r.destination },
  { header: 'Lead type', value: (r) => (r.channel.isPaid ? 'Inorganic' : 'Organic') },
  { header: 'Platform', value: (r) => r.channel.platform },
  { header: 'Campaign', value: (r) => r.campaign },
  { header: 'Connection', value: (r) => r.connection.name },
  { header: 'Agent', value: (r) => r.assignment.agentName ?? 'Unassigned' },
  { header: 'Assigned', value: (r) => istDateTime(r.assignment.assignedAt) },
  { header: 'Assigned by', value: (r) => r.assignment.method },
  { header: 'CRM status', value: (r) => r.forward.status },
  { header: 'CRM lead', value: (r) => r.forward.crmLeadReference },
  { header: 'Message', value: (r) => r.message },
];

/**
 * Download the leads behind one of the destination summary's counts as an Excel file.
 * Reads every page of the queue for the window, then keeps exactly the rows
 * the summary counts: duplicates are dropped, and "cumulative" keeps only
 * leads an agent actually holds (the queue's assigned window also lists the
 * window's still-unassigned arrivals). Returns how many leads were written;
 * 0 means there was nothing to export and no file was produced.
 */
export const exportLpsLeads = async (args: {
  kind: LpsLeadExportKind;
  from?: string;
  to?: string;
  leadType?: LpsLeadType;
}): Promise<number> => {
  const window: Partial<LpsLeadListQuery> =
    args.kind === 'cumulative'
      ? {
          ...(args.from ? { assignedFrom: args.from } : {}),
          ...(args.to ? { assignedTo: args.to } : {}),
        }
      : { ...(args.from ? { from: args.from } : {}), ...(args.to ? { to: args.to } : {}) };

  const rows: LpsLeadItem[] = [];
  for (let page = 1; ; page += 1) {
    const res = await listLpsLeads({
      page,
      pageSize: PAGE_SIZE,
      sortBy: 'receivedAt',
      sortDir: 'asc',
      ...(args.leadType ? { leadType: args.leadType } : {}),
      ...window,
    });
    rows.push(...res.data);
    if (!res.pagination.hasNext) break;
  }

  const counted = rows.filter(
    (r) => r.status !== 'DUPLICATE' && (args.kind === 'received' || r.assignment.agentId !== null),
  );
  if (counted.length === 0) return 0;

  const span = args.from
    ? args.to && args.to !== args.from
      ? `${args.from}_to_${args.to}`
      : args.from
    : 'all-time';
  downloadExcel(
    counted,
    COLUMNS,
    `lps-${args.kind === 'cumulative' ? 'cumulative' : 'received'}-leads-${span}`,
  );
  return counted.length;
};
